import { ReactNode, useEffect, useRef } from "react";
import classNames from "classnames";
import List from "app/pages/.shared/List";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ALERT_TYPE } from "app/constants";
import { scrollToElement } from "app/utils/scroller";
import {
	AlertCircleFilledIcon,
	CheckmarkCircleFilledIcon,
	InfoCircleFilledIcon,
	WarningFilledIcon,
} from "app/icons/navigation";
import { IdeaStrokeIcon } from "app/icons/profile";
import "./AlertMessage.scss";

export type AlertMessageType = (typeof ALERT_TYPE)[keyof typeof ALERT_TYPE];

const ICON_BY_ALERT_TYPE: Partial<Record<AlertMessageType, typeof InfoCircleFilledIcon>> = {
	[ALERT_TYPE.NEUTRAL]: IdeaStrokeIcon,
	[ALERT_TYPE.INFO]: InfoCircleFilledIcon,
	[ALERT_TYPE.ERROR]: AlertCircleFilledIcon,
	[ALERT_TYPE.WARNING]: WarningFilledIcon,
	[ALERT_TYPE.SUCCESS]: CheckmarkCircleFilledIcon,
};

type AlertMessageProps = {
	message?: ReactNode;
	errors?: ReactNode[];
	alertType?: AlertMessageType;
	children?: ReactNode;
	scrollOnShow?: boolean;
	className?: string;
	withIcon?: boolean;
};

const AlertMessage = ({
	errors = [],
	message,
	alertType = ALERT_TYPE.INFO,
	children,
	scrollOnShow = false,
	className,
	withIcon = true,
}: AlertMessageProps) => {
	const Icon = ICON_BY_ALERT_TYPE[alertType] || InfoCircleFilledIcon;

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (ref.current && scrollOnShow) {
			scrollToElement({ element: ref.current, offset: -20, disableSmoothScroll: false });
		}
	}, [scrollOnShow]);

	return (
		<div
			ref={ref}
			role="alert"
			className={classNames("alert-message", className, {
				"alert-message--neutral": alertType === ALERT_TYPE.NEUTRAL,
				"alert-message--error": alertType === ALERT_TYPE.ERROR,
				"alert-message--success": alertType === ALERT_TYPE.SUCCESS,
				"alert-message--warning": alertType === ALERT_TYPE.WARNING,
			})}
		>
			{message && (
				<div className="alert-message__message">
					{withIcon && <Icon data-testid="alert-message-icon" />}
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="alert-message__text"
						data-testid="alert-message"
					>
						{message}
					</Typography>
				</div>
			)}
			{children}
			{errors.length > 0 ? <List items={errors} /> : false}
		</div>
	);
};

export default AlertMessage;
